import React, { useState, useCallback, useEffect } from 'react';
import './CapturePage.css';
import { Language, t } from '../../utils/i18n';
import { addSparkToCache } from '../../utils/sparkCache';

interface SparkData {
  title: string;
  project_tags: string[]; // 改为数组，支持多选
  description?: string;
  creator_comment?: string;
}

// 5个标签定义（支持中英文）
const IDEA_TAGS_ZH = [
  { id: 'new-activity', label: '新活动', icon: '🎉' },
  { id: 'new-idea', label: '新创意', icon: '💡' },
  { id: 'new-thought', label: '新思想', icon: '🧠' },
  { id: 'new-paradigm', label: '新模态', icon: '🔄' },
  { id: 'daily-new', label: '日日新', icon: '☀️' }
];

const IDEA_TAGS_EN = [
  { id: 'new-activity', label: 'New Activity', icon: '🎉' },
  { id: 'new-idea', label: 'New Idea', icon: '💡' },
  { id: 'new-thought', label: 'New Thought', icon: '🧠' },
  { id: 'new-paradigm', label: 'New Paradigm', icon: '🔄' },
  { id: 'daily-new', label: 'Daily New', icon: '☀️' }
];

interface CapturePageProps {
  onBackToHome: () => void;
  onNavigateToBoard: () => void;
  lang: Language;
}

export default function CapturePage({ onBackToHome, onNavigateToBoard, lang }: CapturePageProps) {
  const [formData, setFormData] = useState<SparkData>({
    title: '',
    project_tags: ['new-idea'], // 默认选中"新创意"
    description: '',
    creator_comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // 添加组件挂载日志
  useEffect(() => {
    console.log('🟢 CapturePage MOUNTED - Timestamp:', Date.now());

    // 添加全局焦点监听，找出到底是什么在抢焦点
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      console.log('🎯 Focus moved to:', target.tagName, target.id, target.className);
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      console.log('🔴 CapturePage UNMOUNTED - Timestamp:', Date.now());
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, []);

  // 使用useCallback来稳定onChange函数，防止每次渲染都创建新函数
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('✏️ Title changed:', e.target.value);
    setFormData(prev => ({ ...prev, title: e.target.value }));
  }, []);

  // 标签切换（多选）
  const handleTagToggle = useCallback((tagId: string) => {
    setFormData(prev => {
      const isSelected = prev.project_tags.includes(tagId);
      const newTags = isSelected
        ? prev.project_tags.filter(t => t !== tagId)
        : [...prev.project_tags, tagId];

      console.log(`🏷️ Tag toggled: ${tagId}, selected: ${!isSelected}, all tags:`, newTags);
      return { ...prev, project_tags: newTags };
    });
  }, []);

  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    console.log('✏️ Description changed');
    setFormData(prev => ({ ...prev, description: e.target.value }));
  }, []);

  const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('✏️ Comment changed');
    setFormData(prev => ({ ...prev, creator_comment: e.target.value }));
  }, []);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Please enter a title' });
      return;
    }

    // 至少选择一个标签
    if (formData.project_tags.length === 0) {
      setMessage({ type: 'error', text: lang === 'zh' ? '请至少选择一个标签' : 'Please select at least one tag' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);
    console.log('🚀 Submitting spark:', formData);

    // 重试机制：最多重试3次
    const MAX_RETRIES = 3;
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`📤 Attempt ${attempt}/${MAX_RETRIES}...`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 增加到8秒

        const response = await fetch('/api/sparks', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: formData.title.trim(),
            project_tag: formData.project_tags.join(','), // 数组转为逗号分隔字符串
            description: formData.description.trim() || undefined,
            creator_comment: formData.creator_comment.trim() || undefined
          }),
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        console.log(`✅ API response status: ${response.status}`);
        const data = await response.json();
        console.log('📦 API response data:', data);

        if (response.ok && data.success) {
          setMessage({ type: 'success', text: t('captureSuccess', lang) });

          // 添加到本地缓存
          if (data.spark) {
            addSparkToCache(data.spark);
          }

          // 重置表单，保留默认标签
          setFormData({
            title: '',
            project_tags: ['new-idea'], // 恢复默认
            description: '',
            creator_comment: ''
          });

          // Auto navigate to board after success
          setTimeout(() => {
            onNavigateToBoard();
          }, 1000);
          return; // 成功，退出重试循环
        } else {
          console.error('❌ API error:', data);
          lastError = new Error(data.error || '捕获失败');
          // API返回错误，不重试
          break;
        }
      } catch (error) {
        lastError = error as Error;
        console.error(`❌ Attempt ${attempt} failed:`, error);

        // 最后一次尝试失败，不再重试
        if (attempt === MAX_RETRIES) {
          break;
        }

        // 等待1秒后重试
        console.log(`⏳ Waiting 1s before retry...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    // 所有尝试都失败
    console.error('❌ All attempts failed');
    if (lastError) {
      if (lastError.name === 'AbortError') {
        setMessage({ type: 'error', text: t('timeoutError', lang) });
      } else {
        setMessage({ type: 'error', text: `${t('networkError', lang)} ${lastError.message}` });
      }
    }

    setIsSubmitting(false);
  }, [formData, onNavigateToBoard, lang]);

  // 添加键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter') {
        handleSubmit(e as any);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit]);

  return (
    <div className="spark-capture-container">
      <div className="spark-capture-card">
        <div className="spark-header">
          <div className="header-with-back">
            <button onClick={onBackToHome} className="btn-back">
              ← {t('backToHome', lang)}
            </button>
            <div className="header-text">
              <h1>{t('sparkCapture', lang)} - NEW CODE VERSION 2.0</h1>
              <p className="spark-subtitle">{t('sparkCaptureSubtitle', lang)}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="spark-form">
          <div className="form-group">
            <label htmlFor="spark-title">
              {t('title', lang)} <span className="required">{t('required', lang)}</span>
            </label>
            <input
              id="spark-title"
              type="text"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder={lang === 'zh' ? '用一句话描述你的想法...' : 'Describe your idea in one sentence...'}
              className="form-input"
              maxLength={200}
              disabled={isSubmitting}
              required
            />
            <span className="char-count">{formData.title.length}/200</span>
          </div>

          <div className="form-group">
            <label>
              {lang === 'zh' ? '标签' : 'Tags'} <span className="required">*</span>
            </label>
            <div className="idea-tags-container">
              {(lang === 'zh' ? IDEA_TAGS_ZH : IDEA_TAGS_EN).map(tag => {
                const isSelected = formData.project_tags.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagToggle(tag.id)}
                    disabled={isSubmitting}
                    className={`idea-tag-button ${isSelected ? 'selected' : ''}`}
                  >
                    <span className="tag-icon">{tag.icon}</span>
                    <span className="tag-label">{tag.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="tag-hint">
              {lang === 'zh' ? '💡 提示：可以同时选择多个标签' : '💡 Tip: You can select multiple tags'}
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('description', lang)} {t('optional', lang)}</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={handleDescriptionChange}
              placeholder={lang === 'zh' ? '详细描述你的想法...' : 'Elaborate on your idea...'}
              className="form-textarea"
              rows={4}
              maxLength={1000}
              disabled={isSubmitting}
            />
            <span className="char-count">{formData.description.length}/1000</span>
          </div>

          <div className="form-group">
            <label htmlFor="comment">{t('notes', lang)} {t('optional', lang)}</label>
            <input
              id="comment"
              type="text"
              value={formData.creator_comment}
              onChange={handleCommentChange}
              placeholder={lang === 'zh' ? '任何备注...' : 'Any notes...'}
              className="form-input"
              maxLength={200}
              disabled={isSubmitting}
            />
          </div>

          {message && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !formData.title.trim()}
            >
              {isSubmitting ? t('capturing', lang) : t('quickCapture', lang)}
            </button>

            <button
              type="button"
              onClick={onNavigateToBoard}
              className="btn btn-secondary"
            >
              {t('viewBoard', lang)}
            </button>
          </div>

          <div className="shortcut-hint">
            {t('tipShortcut', lang)}
          </div>
        </form>
      </div>
    </div>
  );
}
// 2026年01月18日 19:47:16
